import {
  blockConfig,
  blockMinimum,
  isBlockPart,
  selectionEntities,
} from "./blocks";
import { clampZoom } from "./viewport";
import { remapCopiedGroups } from "./groups";
import { gridPoint, gridMove, type GridSettings } from "./grid";
import type { Point, SceneNode } from "../scene/model";
import type { AffineMatrix } from "../geometry/affine";
import { createNativeNode } from "./document";
import type { ShapeTool } from "./shapes";
import type { NativeSession } from "./session";
import {
  documentPoint,
  drawBox,
  hitFrameHandle,
  hitRotation,
  hitNode,
  marqueeNodes,
  selectionFrame,
  handles,
  withBox,
  type Box,
  type Camera,
} from "./geometry";
import {
  multiply,
  resizeMatrix,
  rotationMatrix,
  transformNativeNode,
} from "./transforms";

export type NativeTool = "select" | ShapeTool;
interface Gesture {
  id: number;
  kind: "select" | "draw" | "move" | "resize" | "rotate" | "marquee" | "pan";
  start: Point;
  screen: Point;
  last: Point;
  camera: Camera;
  nodes: SceneNode[];
  base: SceneNode[];
  handle: number;
  copy: boolean;
  moved: boolean;
  selection: string[];
  toggle: string | null;
  frame: AffineMatrix | null;
  preview: AffineMatrix | null;
  marquee: Box | null;
}
export class NativePointer {
  tool: NativeTool = "select";
  space = false;
  external = false;
  wheelEnabled: (event: WheelEvent) => boolean = () => true;
  onToolChange?: () => void;
  moveDrop?: {
    update(point: Point): void;
    drop(): boolean;
    cancel(): void;
  };
  cursor = "default";
  private gesture: Gesture | null = null;
  constructor(
    private canvas: HTMLCanvasElement,
    private session: NativeSession,
    readonly camera: Camera,
    private changed: () => void,
    private grid?: GridSettings,
  ) {
    canvas.addEventListener("pointerdown", (e) => this.down(e));
    canvas.addEventListener("pointermove", (e) => this.move(e));
    canvas.addEventListener("pointerup", (e) => this.up(e));
    canvas.addEventListener("pointercancel", () => this.cancel());
    canvas.addEventListener("lostpointercapture", () => this.cancel());
    canvas.addEventListener(
      "wheel",
      (e) => {
        if (!this.wheelEnabled(e)) return;
        e.preventDefault();
        if (this.gesture) return;
        const p = this.screen(e),
          anchor = documentPoint(p, camera);
        camera.zoom = clampZoom(camera.zoom * Math.exp(-e.deltaY * 0.001));
        camera.x = p.x - anchor.x * camera.zoom;
        camera.y = p.y - anchor.y * camera.zoom;
        changed();
      },
      { passive: false },
    );
    for (const type of ["keydown", "keyup"] as const)
      window.addEventListener(type, (event) => {
        const g = this.gesture;
        if (g?.kind !== "draw" || !["Shift", "Alt"].includes(event.key)) return;
        event.preventDefault();
        if (g.moved) {
          this.draw(g, g.last, event.shiftKey, event.altKey);
          this.changed();
        }
      });
    window.addEventListener("blur", () => {
      this.space = false;
      this.cancel();
      this.changed();
    });
  }
  get active() {
    return !!this.gesture;
  }
  get frame() {
    return (
      this.gesture?.preview ??
      (this.external ? null : selectionFrame(this.session.selectedNodes))
    );
  }
  get marquee() {
    return this.gesture?.marquee ?? null;
  }
  setTool(tool: NativeTool) {
    this.onToolChange?.();
    this.external = false;
    this.cancel();
    this.tool = tool;
    this.cursor = tool === "select" ? "default" : "crosshair";
    this.changed();
  }
  cancel() {
    this.moveDrop?.cancel();
    const g = this.gesture;
    if (!g) return;
    this.gesture = null;
    if (g.kind === "pan") Object.assign(this.camera, g.camera);
    else this.session.cancel();
    if (this.canvas.hasPointerCapture(g.id))
      this.canvas.releasePointerCapture(g.id);
    this.changed();
  }
  private screen(event: MouseEvent): Point {
    const b = this.canvas.getBoundingClientRect();
    return { x: event.clientX - b.left, y: event.clientY - b.top };
  }
  private down(event: PointerEvent) {
    if (
      this.gesture ||
      (event.button !== 0 && event.button !== 1) ||
      (this.external &&
        !event.ctrlKey &&
        !event.metaKey &&
        !this.space &&
        event.button !== 1)
    )
      return;
    event.preventDefault();
    this.canvas.focus();
    const screen = this.screen(event),
      start = documentPoint(screen, this.camera);
    const g: Gesture = {
      id: event.pointerId,
      kind: "move",
      start,
      screen,
      last: start,
      camera: { ...this.camera },
      nodes: [],
      base: this.session.page.nodes,
      handle: -1,
      copy: false,
      moved: false,
      selection: this.session.selectedIds.slice(),
      toggle: null,
      frame: null,
      preview: null,
      marquee: null,
    };
    if (this.space || event.button === 1) g.kind = "pan";
    else {
      this.session.begin();
      const selecting =
          this.tool === "select" || event.ctrlKey || event.metaKey,
        frame = selectionFrame(this.session.selectedNodes),
        touch = event.pointerType === "touch";
      const handle =
        selecting && frame
          ? hitFrameHandle(frame, start, this.camera.zoom, touch)
          : -1;
      const creationTarget = selecting
        ? null
        : hitNode(this.session.page.nodes, start, this.camera.zoom);
      if (
        creationTarget &&
        ["rectangle", "ellipse", "polygon"].includes(creationTarget.kind)
      ) {
        // DRAW-11: this press only selects, as in the original creation tool.
        // Consume its release too; the next gesture can move/resize the figure.
        this.setTool("select");
        this.session.select([creationTarget.id]);
        g.kind = "select";
      } else if (handle >= 0) {
        g.kind = "resize";
        g.handle = handle;
      } else if (
        selecting &&
        frame &&
        hitRotation(frame, start, this.camera.zoom, touch)
      )
        g.kind = "rotate";
      else if (!selecting && this.tool !== "select") {
        g.kind = "draw";
        if (this.grid)
          g.start = gridPoint(
            start,
            {
              x: this.session.page.size.width,
              y: this.session.page.size.height,
            },
            this.camera.zoom,
            this.grid,
          );
        g.nodes = [
          createNativeNode(
            crypto.randomUUID(),
            this.tool,
            start.x,
            start.y,
            1,
            1,
          ),
        ];
        this.session.select([]);
      } else {
        const node = hitNode(this.session.page.nodes, start, this.camera.zoom);
        if (!node) {
          g.kind = "marquee";
          if (!event.shiftKey) this.session.select([]);
          else g.selection = this.session.selectedIds.slice();
        } else if (event.shiftKey) {
          if (this.session.selectedIds.includes(node.id)) g.toggle = node.id;
          else this.session.select([...this.session.selectedIds, node.id]);
        } else if (!this.session.selectedIds.includes(node.id))
          this.session.select([node.id]);
      }
      if (["move", "resize", "rotate"].includes(g.kind)) {
        g.nodes = this.session.selectedNodes;
        g.frame = selectionFrame(g.nodes);
        g.preview = g.frame;
        if (event.altKey && g.kind === "move") {
          g.copy = true;
          const sourceIds = g.nodes.map((n) => n.id);
          g.nodes = g.nodes.map((n) => ({
            ...structuredClone(n),
            id: crypto.randomUUID(),
          }));
          remapCopiedGroups(g.nodes, this.session.page.nodes, sourceIds, () =>
            crypto.randomUUID(),
          );
        }
      }
    }
    this.gesture = g;
    this.canvas.setPointerCapture(event.pointerId);
    this.changed();
  }
  private hover(event: PointerEvent) {
    const p = documentPoint(this.screen(event), this.camera),
      frame = this.frame,
      selecting = this.tool === "select" || event.ctrlKey || event.metaKey;
    let cursor = selecting ? "default" : "crosshair";
    if (selecting && frame) {
      const h = hitFrameHandle(
        frame,
        p,
        this.camera.zoom,
        event.pointerType === "touch",
      );
      if (h >= 0) {
        const [x, y] = handles[h]!,
          angle =
            (Math.atan2(
              frame[1] * x + frame[3] * y,
              frame[0] * x + frame[2] * y,
            ) *
              180) /
            Math.PI;
        cursor = ["ew-resize", "nwse-resize", "ns-resize", "nesw-resize"][
          ((Math.round(angle / 45) % 4) + 4) % 4
        ]!;
      } else if (
        hitRotation(frame, p, this.camera.zoom, event.pointerType === "touch")
      )
        cursor = "grab";
      else if (hitNode(this.session.page.nodes, p, this.camera.zoom))
        cursor = "move";
    }
    if (cursor !== this.cursor) {
      this.cursor = cursor;
      this.changed();
    }
  }
  private move(event: PointerEvent) {
    const g = this.gesture;
    if (!g) {
      this.hover(event);
      return;
    }
    if (g.id !== event.pointerId) return;
    if (g.kind === "select") return;
    const screen = this.screen(event);
    if (
      !g.moved &&
      Math.hypot(screen.x - g.screen.x, screen.y - g.screen.y) < 3
    )
      return;
    g.moved = true;
    if (g.kind === "move" && g.nodes.length)
      this.moveDrop?.update({ x: event.clientX, y: event.clientY });
    if (g.kind === "pan") {
      this.camera.x = g.camera.x + screen.x - g.screen.x;
      this.camera.y = g.camera.y + screen.y - g.screen.y;
    } else {
      let point = documentPoint(screen, g.camera);
      const size = {
        x: this.session.page.size.width,
        y: this.session.page.size.height,
      };
      if (this.grid && (g.kind === "draw" || g.kind === "resize"))
        point = gridPoint(point, size, g.camera.zoom, this.grid);
      let dx = point.x - g.start.x,
        dy = point.y - g.start.y;
      if (g.kind === "marquee") {
        g.marquee = drawBox(g.start, point, true, false);
        this.session.select([
          ...(event.shiftKey ? g.selection : []),
          ...marqueeNodes(g.base, g.marquee).map((n) => n.id),
        ]);
      } else if (g.kind === "draw") {
        g.last = point;
        this.draw(g, point, event.shiftKey, event.altKey);
      } else {
        let nodes: SceneNode[], matrix: AffineMatrix;
        if (g.kind === "move") {
          if (event.shiftKey) {
            if (Math.abs(dx) >= Math.abs(dy)) dy = 0;
            else dx = 0;
          }
          if (this.grid) {
            const snapped = gridMove(
              g.nodes,
              { x: dx, y: dy },
              size,
              g.camera.zoom,
              this.grid,
            );
            dx = snapped.x;
            dy = snapped.y;
          }
          matrix = [1, 0, 0, 1, dx, dy];
          nodes = g.nodes.map((n) => ({
            ...n,
            transform: {
              ...n.transform,
              x: n.transform.x! + dx,
              y: n.transform.y! + dy,
            },
          }));
        } else {
          if (!g.frame) return;
          const minimum =
            g.nodes.length === 1
              ? {
                  x: 1 / g.nodes[0]!.geometry.width!,
                  y: 1 / g.nodes[0]!.geometry.height!,
                }
              : {
                  x: Math.max(
                    ...g.nodes.flatMap((n) => [
                      1 / n.geometry.width!,
                      1 / n.geometry.height!,
                    ]),
                  ),
                  y: Math.max(
                    ...g.nodes.flatMap((n) => [
                      1 / n.geometry.width!,
                      1 / n.geometry.height!,
                    ]),
                  ),
                };
          for (const n of selectionEntities(g.nodes)) {
            const c = blockConfig(n);
            if (c) {
              const m = blockMinimum(c);
              minimum.x = Math.max(minimum.x, m.width / n.geometry.width!);
              minimum.y = Math.max(minimum.y, m.height / n.geometry.height!);
            }
          }
          matrix =
            g.kind === "rotate"
              ? rotationMatrix(g.frame, g.start, point, event.shiftKey)
              : resizeMatrix(
                  g.frame,
                  handles[g.handle]!,
                  { x: dx, y: dy },
                  event.shiftKey,
                  event.altKey,
                  minimum,
                );
          try {
            nodes = g.nodes.map((n) => transformNativeNode(n, matrix));
          } catch {
            return;
          }
        }
        const changes = new Map(nodes.map((n) => [n.id, n]));
        this.session.replaceNodes(
          g.copy
            ? [...g.base, ...nodes]
            : g.base.map((n) => changes.get(n.id) ?? n),
        );
        this.session.select(nodes.map((n) => n.id));
        if (g.frame) g.preview = multiply(matrix, g.frame);
      }
    }
    this.changed();
  }
  private draw(
    g: Gesture,
    point: Point,
    constrained: boolean,
    centered: boolean,
  ) {
    // Replay from the initial anchor, including modifier changes without pointer motion.
    const box = drawBox(g.start, point, !constrained, centered);
    if (box.width < 1 || box.height < 1) return;
    const node = withBox(g.nodes[0]!, box);
    this.session.replaceNodes([...g.base, node]);
    this.session.select([node.id]);
  }
  private up(event: PointerEvent) {
    const g = this.gesture;
    if (!g || g.id !== event.pointerId) return;
    this.move(event);
    this.gesture = null;
    if (!g.moved && g.toggle)
      this.session.select(
        this.session.selectedIds.filter((id) => {
          const clicked = this.session.page.nodes.find(
            (n) => n.id === g.toggle,
          );
          return clicked && isBlockPart(clicked)
            ? !this.session.page.nodes.some(
                (n) => n.id === id && n.layer.groupId === clicked.layer.groupId,
              )
            : id !== g.toggle;
        }),
      );
    const dropped = g.kind === "move" && g.moved && this.moveDrop?.drop();
    if (g.kind !== "pan" && !dropped) this.session.finish();
    if (this.canvas.hasPointerCapture(g.id))
      this.canvas.releasePointerCapture(g.id);
    this.changed();
  }
}
